import type { CatalogPlatform, MetricDefinition, MetricsCatalogResponse } from "./types";
import { SubscriptionType } from "@/pages/subscriptions/constants";

const PLATFORM_DISPLAY_LABELS: Record<CatalogPlatform, string> = {
  snowflake: "Snowflake",
  databricks: "Databricks",
};

/**
 * Flatten the nested metrics catalog response into a flat MetricDefinition array.
 * The API returns entity_types + metrics keyed by entity; this normalizes them
 * into the flat format used throughout the frontend.
 */
export function flattenMetricsCatalog(
  catalog: MetricsCatalogResponse,
  fallbackPlatform?: CatalogPlatform
): MetricDefinition[] {
  const metrics: MetricDefinition[] = [];

  const comparisonTypeKeys = catalog.comparison_types.map(c => c.key);
  const thresholdTypeKeys = catalog.threshold_types.map(t => t.key);
  const dateRangeKeys = catalog.date_ranges.map(d => d.key);

  const defaultComparisonType = comparisonTypeKeys[0] ?? "absolute_value";
  const defaultThresholdType = thresholdTypeKeys[0] ?? "exceeds";
  const defaultDateRange = dateRangeKeys[0] ?? "previous_day";

  const entityLabelMap: Record<string, string> = {};
  const entityPlatformMap: Record<string, CatalogPlatform | undefined> = {};
  for (const et of catalog.entity_types) {
    entityLabelMap[et.key] = et.label;
    entityPlatformMap[et.key] = et.platform;
  }

  for (const [entityKey, entityData] of Object.entries(catalog.metrics)) {
    const entityLabel = entityLabelMap[entityKey] ?? entityKey;
    const entityPlatform = entityPlatformMap[entityKey] ?? fallbackPlatform;
    const entityFilters = entityData.filters ?? [];
    const filterKeys = entityFilters.map(f => f.key);
    const searchableFilterKeys = entityFilters.filter(f => f.searchable === true).map(f => f.key);

    for (const group of entityData.groups) {
      for (const metric of group.metrics) {
        const sourceName = metric.source_name ?? entityPlatform ?? fallbackPlatform;
        const sourceDisplayLabel =
          metric.source_display_label ??
          (sourceName ? PLATFORM_DISPLAY_LABELS[sourceName] : undefined);
        metrics.push({
          name: metric.key,
          display_name: metric.label,
          description: metric.description ?? "",
          category: entityKey,
          category_display_name: entityLabel,
          source_name: sourceName,
          source_display_label: sourceDisplayLabel,
          entity_type: entityKey,
          unit: metric.unit,
          supports_comparison_types: comparisonTypeKeys,
          supports_threshold_types: thresholdTypeKeys,
          supports_date_ranges:
            metric.supported_date_ranges && metric.supported_date_ranges.length > 0
              ? metric.supported_date_ranges
              : dateRangeKeys,
          supports_aggregations: metric.aggregations,
          supported_filters: filterKeys,
          searchable_filters: searchableFilterKeys,
          supported_subscription_types: [SubscriptionType.REPORT, SubscriptionType.ALERT],
          default_comparison_type: defaultComparisonType,
          default_threshold_type: defaultThresholdType,
          default_threshold: "100",
          default_date_range: defaultDateRange,
          default_aggregation: metric.aggregations[0] ?? "sum",
        });
      }
    }
  }

  return metrics;
}
